import { Fade, Typography, Box } from '@mui/material';
import {
  StyledDatePickerContainer,
  StyledPickersContainer,
  StyledPickersDivider,
} from '../DatePicker/styles';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslationContext } from '../../context/TranslationContext';

const DATE_FORMAT = 'DD/MM/YYYY';
const TIME_FORMAT = 'HH:mm';

type Props = {
  selectedDateTime: Dayjs;
  handleSelect: (date: Dayjs) => void;
  disabled: boolean;
  isError?: boolean;
  hideLabel?: boolean;
};

const TaskDatePicker = ({
  selectedDateTime,
  handleSelect,
  disabled,
  isError = false,
  hideLabel = false,
}: Props) => {
  const t = useTranslationContext();

  const handleSelectDate = (newDate: Dayjs | null) => {
    if (!newDate) return;

    // Mantengo la hora/min de lo que ya estaba elegido
    const merged = newDate.hour(selectedDateTime.hour()).minute(selectedDateTime.minute());

    handleSelect(merged);
  };

  const handleSelectTime = (newTime: Dayjs | null) => {
    if (!newTime) return;

    // Mantengo el día, cambio solo hora/min
    const merged = selectedDateTime.hour(newTime.hour()).minute(newTime.minute());

    handleSelect(merged);
  };

  // Modo vista: mostrar la fecha y hora como texto estático
  if (disabled) {
    return (
      <StyledDatePickerContainer>
        {!hideLabel && (
          <span
            style={{
              userSelect: 'none',
              marginBottom: 10,
            }}
          >
            {t('global.date')}
          </span>
        )}

        <Box>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {selectedDateTime.format(DATE_FORMAT)}
          </Typography>
          <Typography variant="body1">{selectedDateTime.format(TIME_FORMAT)}</Typography>
        </Box>
      </StyledDatePickerContainer>
    );
  }

  return (
    <StyledDatePickerContainer>
      {!hideLabel && (
        <span
          style={{
            userSelect: 'none',
            marginBottom: 10,
          }}
        >
          {t('global.date')}
        </span>
      )}

      <StyledPickersContainer>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            disabled={disabled}
            value={selectedDateTime}
            onChange={handleSelectDate}
            slotProps={{
              textField: () => ({
                label: t('global.date'),
                size: 'small',
                className: 'smallInput',
              }),
              popper: {
                sx: {
                  '& .MuiPaper-root': {
                    //@ts-ignore
                    backgroundColor: (theme) => theme.palette.background.panel,
                  },
                },
              },
            }}
            sx={{ alignSelf: 'baseline', flex: 1 }}
            minDate={dayjs()}
          />

          <StyledPickersDivider variant="body1">_</StyledPickersDivider>

          <TimePicker
            disabled={disabled}
            value={selectedDateTime}
            onChange={handleSelectTime}
            ampm={false}
            slotProps={{
              textField: () => ({
                label: t('global.time'),
                size: 'small',
                className: 'smallInput',
              }),
              popper: {
                sx: {
                  '& .MuiPaper-root': {
                    //@ts-ignore
                    backgroundColor: (theme) => theme.palette.background.panel,
                  },
                },
              },
            }}
            sx={{ alignSelf: 'baseline', flex: 1 }}
          />
        </LocalizationProvider>
      </StyledPickersContainer>
      <Fade in={isError}>
        <Typography color={'red'} variant="caption">
          {t('conversations.sidebar.events.errorDate')}
        </Typography>
      </Fade>
    </StyledDatePickerContainer>
  );
};

export default TaskDatePicker;
